import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppState } from 'context/AppState';
import TopBarPattern from 'assets/icons/topbar_pattern.svg';
import { isRussia } from 'utils/countryCode';
import { isOsrSummarySupportedCrop } from 'pages/RecommendationV2/utils/isOsrSummarySupportedCrop';
import { useBreakpoint } from 'hooks';
import { useSkeletonLoader, getCropName } from '../HeroBanner/heroBannerUtils';
import { NonDDGUserDetails } from '../HeroBanner/NonDDGUserDetails';
import FieldDetails from '../HeroBanner/FieldDetails';
import ReportOverviewContent from '../ReportOverview/ReportOverviewContent';
import {
  HeroBanner0bWrapper,
  HeroBanner0bSection,
  HeroBanner0bFieldInfo,
  HeroBanner0bReportOverview,
  ExpandIcon,
  CollapsibleHeader,
  HeaderTitle,
  ExpandedContent,
  HeroBanner0bUserDetailsNonDDG,
  HeroBanner0bFieldInfoMobilePrimary,
  HeroBanner0bFieldInfoMobileSecondary,
  HeroBanner0bFieldInfoContainer,
} from './HeroBanner0b.styles';

interface HeroBanner0bProps {
  isRecommendationAPILoading: boolean;
  isNonDDG: boolean;
}

const HeroBanner0b = (props: HeroBanner0bProps): JSX.Element => {
  const { isRecommendationAPILoading, isNonDDG } = props;
  const { isMobile } = useBreakpoint();
  const [t] = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const {
    apiData: { recommendationCropwise, nonDDGUserDetails },
  } = useAppState();

  const recommendationCW = recommendationCropwise?.recommendations[0];
  const fieldSummaryArea = recommendationCropwise ? recommendationCropwise.summary_area : '';
  const isDDG = recommendationCropwise?.source === 'DDG';

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCollapseChange = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
  };

  const SkeletonLoader = useSkeletonLoader();
  const cropName = getCropName(recommendationCW, recommendationCropwise, t);
  const isAISummarySupported =
    isOsrSummarySupportedCrop(cropName) && !isRussia(recommendationCropwise?.country) && !isDDG;

  const totalFields = recommendationCW?.multi_field?.assignments.length ?? 0;
  const loading = isRecommendationAPILoading;

  const renderNonDDGUserDetails = () => (
    <NonDDGUserDetails nonDDGUserDetails={nonDDGUserDetails} isMobile={isMobile} t={t}>
      {(content) => (
        <HeroBanner0bUserDetailsNonDDG
          isMobile={isMobile}
          data-testid="non-ddg-user-contact-details"
        >
          {content}
        </HeroBanner0bUserDetailsNonDDG>
      )}
    </NonDDGUserDetails>
  );

  return (
    <HeroBanner0bWrapper cropName={recommendationCW?.crop_name?.toLowerCase() ?? ''}>
      {loading ? (
        SkeletonLoader
      ) : (
        <HeroBanner0bSection isMobile={isMobile}>
          <CollapsibleHeader onClick={toggleExpanded} isExpanded={isExpanded}>
            <HeaderTitle>{t('Recommendation Details')}</HeaderTitle>
            <ExpandIcon isExpanded={isExpanded}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 10L12 15L17 10"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ExpandIcon>
          </CollapsibleHeader>

          <ExpandedContent isExpanded={isExpanded}>
            {isNonDDG && renderNonDDGUserDetails()}
          </ExpandedContent>

          {!isMobile && <img src={TopBarPattern} alt="No img found" className="leaflet-image" />}

          {isExpanded &&
            (isMobile ? (
              <FieldDetails
                totalFields={totalFields}
                fieldSummaryArea={fieldSummaryArea}
                cropNameLowerCase={recommendationCW?.crop_name.toLowerCase() ?? ''}
                cropName={cropName}
                t={t}
                isMobile={true}
                renderMobilePrimary={(content) => (
                  <HeroBanner0bFieldInfoContainer>
                    <HeroBanner0bFieldInfoMobilePrimary data-testid="hero-banner-field-infomobile">
                      {content}
                    </HeroBanner0bFieldInfoMobilePrimary>
                  </HeroBanner0bFieldInfoContainer>
                )}
                renderMobileSecondary={(content) => (
                  <HeroBanner0bFieldInfoContainer>
                    <HeroBanner0bFieldInfoMobileSecondary>
                      {content}
                    </HeroBanner0bFieldInfoMobileSecondary>
                  </HeroBanner0bFieldInfoContainer>
                )}
              />
            ) : (
              <FieldDetails
                totalFields={totalFields}
                fieldSummaryArea={fieldSummaryArea}
                cropNameLowerCase={recommendationCW?.crop_name.toLowerCase() ?? ''}
                cropName={cropName}
                t={t}
                renderDesktop={(content) => (
                  <HeroBanner0bFieldInfo data-testid="hero-banner-field-info">
                    {content}
                  </HeroBanner0bFieldInfo>
                )}
              />
            ))}

          <HeroBanner0bReportOverview
            data-testid="hero-banner-report-overview"
            isAISummarySupported={isAISummarySupported}
            isCollapsed={isCollapsed}
          >
            <ReportOverviewContent
              isCollapsed={isCollapsed}
              onCollapseChange={handleCollapseChange}
              isAISummarySupported={isAISummarySupported}
            />
          </HeroBanner0bReportOverview>
        </HeroBanner0bSection>
      )}
    </HeroBanner0bWrapper>
  );
};

export default HeroBanner0b;
